import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Label, Grid, SectionHeader, PageHeader, css, Toast, MontoInput } from '../components/UI';
import { Icono, ConIcono } from '../components/Iconos';
import { validarCLABE, hoyISO } from '../utils';
import { Modal } from '../components/UI';
import { parseCFDI, leerArchivo, datosFiscales, soloFiscales } from '../cfdi';


const IMPUESTOS_DEF = [
  { clave: 'IVA_16',        label: 'IVA 16%',              rate: 0.16,      esRetencion: false },
  { clave: 'RET_IVA_10667', label: 'IVA RETENCIÓN 10.67%', rate: 0.106667,  esRetencion: true  },
  { clave: 'RET_IVA_16',    label: 'IVA RETENCIÓN 16%',    rate: 0.16,      esRetencion: true  },
  { clave: 'RET_ISR_125', label: 'RETENCIÓN ISR 1.25%',  rate: 0.0125,  esRetencion: true  },
  { clave: 'RET_ISR_10',  label: 'ISR RETENCIÓN 10%',    rate: 0.10,    esRetencion: true  },
  { clave: 'RET_4',       label: 'RETENCIÓN 4%',         rate: 0.04,    esRetencion: true  },
  { clave: 'RET_IEPS',    label: 'RETENCIÓN IEPS 26.5%', rate: 0.265,   esRetencion: true  },
  { clave: 'IEPS_TRAS',   label: 'IEPS TRASLADADO',      rate: 0,       esRetencion: false },
];

function calcImpuestos(monto, claves = [], importes = null) {
  const base = parseFloat(monto) || 0;
  let iva = 0, retenciones = 0;
  const lineas = [];
  for (const d of IMPUESTOS_DEF) {
    if (!claves.includes(d.clave)) continue;
    const val = importes && importes[d.clave] != null ? importes[d.clave] : base * d.rate;
    lineas.push({ ...d, valor: val });
    if (!d.esRetencion) iva += val; else retenciones += val;
  }
  return { lineas, total: Math.round((base + iva - retenciones) * 100) / 100 };
}

// Selector de motivo de pago: SÓLO deja elegir del catálogo. Lo que se escribe
// filtra la lista, nunca queda como valor — igual que el buscador de clientes de
// aquí abajo. El backend valida lo mismo, porque esto vive en el navegador.
function MotivoSearch({ value, onChange, hasError, motivos }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  // Si el valor cambia desde fuera (limpiar, aplicar plantilla), sigue al padre
  useEffect(() => { setQuery(value || ''); }, [value]);

  const filtered = query.trim()
    ? motivos.filter(m => m.toLowerCase().includes(query.toLowerCase()))
    : motivos;

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Al salir sin haber elegido, se restaura lo último seleccionado: así nunca
  // queda en pantalla un texto que no corresponde al valor real.
  const cerrar = () => { setOpen(false); setQuery(value || ''); };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <input
        value={query}
        placeholder={motivos.length ? 'Busca y elige el motivo de pago...' : 'No hay motivos en el catálogo'}
        disabled={!motivos.length}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (value) onChange(''); }}
        onFocus={() => setOpen(true)}
        onBlur={cerrar}
        style={css.input(hasError)}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--panel)', border: '1px solid var(--hair)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(15,23,42,0.14)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--glass)' }}>
              Sin coincidencias en el catálogo
            </div>
          ) : filtered.map((m, i) => (
            <div key={m} onMouseDown={() => { onChange(m); setQuery(m); setOpen(false); }}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                borderBottom: i < filtered.length - 1 ? '1px solid var(--hair-soft)' : 'none',
                color: 'var(--ink-soft)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--plasma-dim)'; e.currentTarget.style.color = 'var(--plasma)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-soft)'; }}>
              {m}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProveedorSearch({ value, onSelect }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { const d = await api.get(`/clientes?q=${encodeURIComponent(q)}`); setResults(d.items || []); }
    catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(() => search(query), 280); return () => clearTimeout(timer.current); }, [query, search]);
  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      {/* Solo permite seleccionar del catálogo: escribir aquí filtra resultados, nunca fija el valor por sí solo */}
      <input value={query} placeholder="Escribe para buscar cliente en el catálogo..."
        onChange={e => { const v = e.target.value; setQuery(v); setOpen(true); if (value) onSelect(null); }}
        onFocus={() => setOpen(true)}
        style={css.input(false)}
      />
      {open && (results.length > 0 || loading) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: 'var(--panel)', border: '1px solid var(--mesh)', borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,42,0.14)', maxHeight: 260, overflowY: 'auto', marginTop: 4 }}>
          {loading && <div style={{ padding: '10px 14px', color: 'var(--glass)', fontSize: 13 }}>Buscando...</div>}
          {results.map((p, i) => (
            <div key={p._id || i} onMouseDown={() => { onSelect(p); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid var(--hair-soft)' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--plasma-dim)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{p.nombre}</div>
              {p.banco && <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 1 }}>{p.banco} · {p.clabe || '—'}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FacturasTable({ facturas, onChange, hasError }) {
  const upd = (idx, field, val) =>
    onChange(facturas.map((f, i) => i === idx ? { ...f, [field]: val } : f));
  const add = () =>
    onChange([...facturas, { folioCfdi: '', notasCredito: '', monto: '' }]);
  const remove = (idx) => {
    if (facturas.length <= 1) return;
    onChange(facturas.filter((_, i) => i !== idx));
  };
  const fmt = n => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 34px', gap: 6, marginBottom: 4 }}>
        {['Folio CFDI', 'Notas de Crédito', 'Base ($)', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
        ))}
      </div>
      {facturas.map((f, idx) => {
        const montoErr = hasError && (!f.monto || isNaN(parseFloat(f.monto)) || parseFloat(f.monto) <= 0);
        const fromXml = !!f._xml;
        return (
          <div key={idx} style={{ marginBottom: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 34px', gap: 6 }}>
              <input value={f.folioCfdi} onChange={e => upd(idx, 'folioCfdi', e.target.value)}
                placeholder={`CFDI-${String(idx + 1).padStart(3, '0')}`}
                title={f.folioCfdi}
                style={{ ...css.input(!!f._dup), ...(fromXml ? { fontFamily: 'var(--digit-font)', fontSize: 11.5 } : {}) }} />
              <input value={f.notasCredito} onChange={e => upd(idx, 'notasCredito', e.target.value)}
                placeholder="NC-001" style={css.input(false)} />
              <MontoInput value={f.monto} onChange={v => upd(idx, 'monto', v)} err={montoErr} />
              <button aria-label="Quitar factura" title="Quitar factura" onClick={() => remove(idx)} disabled={facturas.length <= 1} style={{
                borderRadius: 7, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)',
                color: 'var(--danger)', cursor: facturas.length <= 1 ? 'not-allowed' : 'pointer',
                fontSize: 14, opacity: facturas.length <= 1 ? 0.3 : 1, fontFamily: 'Inter, sans-serif',
              }}><Icono nombre="papelera" size={15} /></button>
            </div>
            {fromXml && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '3px 2px 0', fontSize: 11.5 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--ok)', fontWeight: 700 }}><Icono nombre="codigo" size={13} />XML</span>
                {f._rfc && <span style={{ color: 'var(--glass)' }}>RFC <b style={{ color: 'var(--ink-soft)' }}>{f._rfc}</b></span>}
                {f._emisor && <span style={{ color: 'var(--glass)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f._emisor}</span>}
                {f._total != null && <span style={{ color: 'var(--glass)' }}>Total CFDI <b style={{ color: 'var(--ink)', fontFamily: 'var(--digit-font)' }}>{fmt(f._total)}</b></span>}
                {f._egreso && <span style={{ color: 'var(--danger)', fontWeight: 700 }}>Nota de crédito (se resta)</span>}
                {f._dup && <span style={{ color: 'var(--danger)', fontWeight: 700 }}><Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{f._dup === 'local' ? 'Repetida en esta lista' : `Ya existe en ${f._dup}`}</span>}
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} style={{
        padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: '1.5px dashed var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)',
        cursor: 'pointer', marginTop: 2, fontFamily: 'Inter, sans-serif',
      }}>+ Agregar factura</button>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Ingresa un monto válido en cada factura</div>}
    </div>
  );
}

export default function NuevaReembolso({ onCreated }) {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [sucursalesAll, setSucursalesAll] = useState([]);
  const [centros, setCentros] = useState([]);
  const [motivosCatalogo, setMotivosCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  const [modoFacturas, setModoFacturas] = useState(false);

  const [form, setForm] = useState({
    empresa: '', sucursal: '', centroCostos: '', direccion: '',
    proveedor: '', rfcProveedor: '', banco: '', clabe: '', cuenta: '', moneda: 'MXN',
    fechaSolicitud: hoyISO(),
    folioCfdi: '', notasCredito: '', monto: '',
    facturas: [],
    formaPago: 'TRANSFERENCIA ELECTRÓNICA', tipoPago: 'Programado', motivoPago: '', observaciones: '',
    impuestos: [], impuestosImportes: {},
  });

  useEffect(() => {
    api.get('/empresas').then(all => {
      if (user?.rol === 'solicitante' && user?.empresas?.length) {
        const propias = all.filter(e => user.empresas.includes(e.nombre));
        setEmpresas(propias);
        if (propias.length === 1) setForm(f => ({ ...f, empresa: propias[0].nombre }));
      } else {
        setEmpresas(all);
      }
    }).catch(() => {});
    api.get('/sucursales').then(setSucursalesAll).catch(() => {});
    api.get('/motivos-reembolso').then(ms => setMotivosCatalogo(ms.map(m => m.nombre))).catch(() => {});
  }, [user]);

  // Cargar centros de costos de la colección CentroCostos filtrada por sucursal
  useEffect(() => {
    if (!form.sucursal) { setCentros([]); return; }
    api.get(`/centros?sucursal=${encodeURIComponent(form.sucursal)}`)
      .then(list => setCentros(
        user?.rol === 'solicitante' && user?.centrosCostos?.length
          ? list.filter(c => user.centrosCostos.includes(c.nombre))
          : list
      ))
      .catch(() => setCentros([]));
  }, [form.sucursal]);

  const empresaObj = empresas.find(e => e.nombre === form.empresa);

  // Sucursales filtradas por compañía; si la empresa no tiene asignadas, muestra todas
  const sucursalesBase = empresaObj?.sucursales?.length
    ? sucursalesAll.filter(s => empresaObj.sucursales.includes(s.nombre))
    : sucursalesAll;
  // Si el solicitante tiene sucursales asignadas, acota a ellas
  const sucursalesFiltradas = (user?.rol === 'solicitante' && user?.sucursales?.length)
    ? sucursalesBase.filter(s => user.sucursales.includes(s.nombre))
    : sucursalesBase;

  const totalBase = modoFacturas
    ? form.facturas.reduce((s, f) => s + (parseFloat(f.monto) || 0) * (f._egreso ? -1 : 1), 0)
    : parseFloat(form.monto) || 0;

  const toggleModo = () => {
    if (!modoFacturas) {
      setForm(f => ({ ...f, facturas: [{ folioCfdi: f.folioCfdi, notasCredito: f.notasCredito, monto: f.monto }] }));
    } else {
      const primera = form.facturas[0] || {};
      setForm(f => ({ ...f, folioCfdi: primera.folioCfdi || '', notasCredito: primera.notasCredito || '', monto: primera.monto || '', facturas: [] }));
    }
    setModoFacturas(m => !m);
    setErrors({});
  };

  // Al editar o quitar facturas se recalculan los impuestos exactos con los XML
  // que permanecen (si quitas un XML, sus impuestos dejan de sumar al total).
  const handleFacturasChange = (facturas) => {
    setForm(f => {
      const importes = {};
      const codigosXml = new Set();
      facturas.forEach(r => {
        Object.entries(r._impuestosImportes || {}).forEach(([k, v]) => {
          importes[k] = (importes[k] || 0) + v;
          codigosXml.add(k);
        });
      });
      // Impuestos marcados a mano (sin importe exacto de XML): se conservan
      const manuales = (f.impuestos || []).filter(c => !(c in (f.impuestosImportes || {})));
      return { ...f, facturas, impuestosImportes: importes, impuestos: [...new Set([...manuales, ...codigosXml])] };
    });
    if (errors.facturas) setErrors(e => ({ ...e, facturas: false }));
  };

  const xmlInputRef = useRef(null);
  const [importando, setImportando] = useState(false);

  const handleImportXML = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportando(true);
    try {
      const nuevas = [];
      const impuestosDetectados = new Set(form.impuestos);
      const importesAcum = { ...(form.impuestosImportes || {}) };
      const errores = [];
      let rfcDistinto = false;

      // Folios ya presentes en la lista actual (para no duplicar)
      const existentes = new Set(
        (modoFacturas ? form.facturas : [{ folioCfdi: form.folioCfdi }])
          .map(f => (f.folioCfdi || '').trim()).filter(Boolean)
      );

      for (const file of files) {
        try {
          const cfdi = parseCFDI(await leerArchivo(file), file.name);
          const folio = cfdi.folioFiscal || cfdi.referencia;
          if (existentes.has(folio)) { errores.push(`${file.name}: repetida`); continue; }
          existentes.add(folio);
          if (form.proveedor && cfdi.rfcEmisor && form.rfcProveedor && cfdi.rfcEmisor !== form.rfcProveedor) rfcDistinto = true;
          cfdi.impuestos.forEach(c => impuestosDetectados.add(c));
          // Nota de crédito (Egreso): sus impuestos restan del neto.
          const sgn = cfdi.esEgreso ? -1 : 1;
          Object.entries(cfdi.impuestosImportes || {}).forEach(([k, v]) => { importesAcum[k] = (importesAcum[k] || 0) + v * sgn; });
          nuevas.push({
            // Nota de crédito: su folio va en "Notas de Crédito", no en "Folio CFDI".
            folioCfdi: cfdi.esEgreso ? '' : folio,
            notasCredito: cfdi.esEgreso ? folio : '',
            monto: cfdi.base ? String(cfdi.base.toFixed(2)) : (cfdi.total ? String(cfdi.total.toFixed(2)) : ''),
            serie: cfdi.serie || '', usoCfdi: cfdi.usoCfdi || '', metodoPago: cfdi.metodoPago || '', fechaTimbrado: cfdi.fechaTimbrado || '',
            ...datosFiscales(cfdi),
            _xml: true, _rfc: cfdi.rfcEmisor, _emisor: cfdi.nombreEmisor, _total: cfdi.total, _egreso: cfdi.esEgreso,
            // Impuestos propios de este CFDI (con signo): permiten restarlos si se quita la factura
            _impuestosImportes: Object.fromEntries(Object.entries(cfdi.impuestosImportes || {}).map(([k, v]) => [k, v * sgn])),
          });
        } catch (e) { errores.push(e.message); }
      }

      if (!nuevas.length) {
        toast_(errores[0] || 'No se pudo leer ningún XML', 'error');
        return;
      }

      // Validar duplicados contra solicitudes ya guardadas
      await Promise.all(nuevas.map(async (f) => {
        try {
          const r = await api.get(`/reembolsos/check-cfdi?folio=${encodeURIComponent(f.folioCfdi)}`);
          if (r.duplicado && r.en) f._dup = r.en.folio;
        } catch {}
      }));
      // Duplicados dentro del propio lote (por si dos archivos traen el mismo UUID)
      const vistos = {};
      nuevas.forEach(f => { const k = f.folioCfdi || f.notasCredito; if (vistos[k]) f._dup = 'local'; vistos[k] = true; });

      // Fusionar con las facturas existentes (descartando renglones vacíos)
      const previas = modoFacturas
        ? form.facturas.filter(f => (f.folioCfdi || '').trim() || (parseFloat(f.monto) || 0) > 0)
        : [];
      const facturas = [...previas, ...nuevas];

      setForm(f => ({ ...f, facturas, impuestos: [...impuestosDetectados], impuestosImportes: importesAcum }));
      setModoFacturas(true);
      setErrors({});

      let msg = `${nuevas.length} factura${nuevas.length !== 1 ? 's' : ''} importada${nuevas.length !== 1 ? 's' : ''}`;
      if (impuestosDetectados.size > form.impuestos.length) msg += ' · impuestos detectados';
      if (errores.length) msg += ` · ${errores.length} omitida${errores.length !== 1 ? 's' : ''}`;
      toast_(msg, errores.length ? 'error' : 'ok');
      if (rfcDistinto) setTimeout(() => toast_('El RFC de algún CFDI no coincide con el proveedor', 'error'), 200);
    } finally {
      setImportando(false);
      if (xmlInputRef.current) xmlInputRef.current.value = '';
    }
  };

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: false })); };

  const handleEmpresa = (nombre) => {
    setForm(f => ({ ...f, empresa: nombre, sucursal: '', centroCostos: '', direccion: '' }));
    if (errors.empresa) setErrors(e => ({ ...e, empresa: false }));
  };
  const handleSucursal = (s) => { set('sucursal', s); set('centroCostos', ''); };
  const handleCentro = (nombre) => {
    const centroObj = centros.find(cc => cc.nombre === nombre);
    const dir = centroObj?.seccion?.trim() || centroObj?.direccion?.trim() || '';
    setForm(f => ({ ...f, centroCostos: nombre, ...(dir ? { direccion: dir } : {}) }));
    if (errors.centroCostos) setErrors(e => ({ ...e, centroCostos: false }));
  };
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaModal, setPlantillaModal] = useState(false);
  const [guardarPlantillaModal, setGuardarPlantillaModal] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState('');

  const cargarPlantillas = useCallback(async () => {
    try { setPlantillas(await api.get('/plantillas')); } catch {}
  }, []);

  useEffect(() => { cargarPlantillas(); }, [cargarPlantillas]);

  const aplicarPlantilla = (p) => {
    setForm(f => ({
      ...f,
      empresa: p.empresa || f.empresa,
      sucursal: p.sucursal || '',
      centroCostos: p.centroCostos || '',
      direccion: p.direccion || '',
      proveedor: p.proveedor || '',
      banco: p.banco || '',
      clabe: p.clabe || '',
      cuenta: p.cuenta || '',
      moneda: p.moneda || 'MXN',
      formaPago: p.formaPago || 'TRANSFERENCIA ELECTRÓNICA',
      tipoPago: p.tipoPago || 'Programado',
      // La plantilla pudo guardarse antes del catálogo, o con un motivo que ya
      // se dio de baja: sólo se aplica si sigue existiendo, para no dejar en el
      // formulario un motivo que el servidor va a rechazar al guardar.
      motivoPago: motivosCatalogo.includes(p.motivoPago) ? p.motivoPago : '',
    }));
    setPlantillaModal(false);
    toast_(motivosCatalogo.includes(p.motivoPago) || !p.motivoPago
      ? `Plantilla "${p.nombre}" aplicada`
      : `Plantilla "${p.nombre}" aplicada · el motivo "${p.motivoPago}" ya no está en el catálogo, elige otro`,
      motivosCatalogo.includes(p.motivoPago) || !p.motivoPago ? 'ok' : 'error');
  };

  const guardarComoPlantilla = async () => {
    if (!nombrePlantilla.trim()) return;
    try {
      await api.post('/plantillas', {
        nombre: nombrePlantilla.trim(),
        empresa: form.empresa, sucursal: form.sucursal, centroCostos: form.centroCostos,
        direccion: form.direccion, proveedor: form.proveedor, banco: form.banco,
        clabe: form.clabe, cuenta: form.cuenta, moneda: form.moneda,
        formaPago: form.formaPago, tipoPago: form.tipoPago,
        motivoPago: form.motivoPago,
      });
      toast_(`Plantilla "${nombrePlantilla.trim()}" guardada`);
      setNombrePlantilla('');
      setGuardarPlantillaModal(false);
      cargarPlantillas();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const eliminarPlantilla = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try { await api.del(`/plantillas/${id}`); cargarPlantillas(); } catch {}
  };

  const [cfdiDuplicado, setCfdiDuplicado] = useState(null);
  const cfdiTimer = useRef(null);

  const checkCfdi = useCallback(async (folio) => {
    if (!folio || folio.length < 3) { setCfdiDuplicado(null); return; }
    try {
      const r = await api.get(`/reembolsos/check-cfdi?folio=${encodeURIComponent(folio)}`);
      setCfdiDuplicado(r.duplicado ? r.en : null);
    } catch { setCfdiDuplicado(null); }
  }, []);


  const handleProveedor = (p) => {
    setForm(f => ({
      ...f,
      proveedor: p ? p.nombre : '',
      rfcProveedor: p?.rfc || '',
      banco: p?.banco || '',
      clabe: p?.clabe || '',
      cuenta: p?.cuenta || '',
      moneda: p?.moneda || 'MXN',
      motivoPago: '',
    }));
    if (p && errors.proveedor) setErrors(e => ({ ...e, proveedor: false }));
  };

  const validate = () => {
    const e = {};
    if (!form.empresa) e.empresa = true;
    if (!form.sucursal) e.sucursal = true;
    if (!form.centroCostos) e.centroCostos = true;
    if (!form.proveedor) e.proveedor = true;
    if (!form.motivoPago.trim()) e.motivoPago = true;
    if (form.observaciones.trim().length < 50) e.observaciones = true; // concepto detallado: mínimo 50 caracteres
    if (modoFacturas) {
      const invalidas = form.facturas.length === 0 ||
        form.facturas.some(f => !f.monto || isNaN(parseFloat(f.monto)) || parseFloat(f.monto) <= 0);
      if (invalidas) e.facturas = true;
    } else {
      if (!form.monto || isNaN(parseFloat(form.monto))) e.monto = true;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const buildPayload = () => modoFacturas
    ? { ...form, monto: totalBase, facturas: form.facturas.map(f => ({ folioCfdi: f.folioCfdi, notasCredito: f.notasCredito, monto: (parseFloat(f.monto) || 0) * (f._egreso ? -1 : 1), serie: f.serie || '', usoCfdi: f.usoCfdi || '', metodoPago: f.metodoPago || '', fechaTimbrado: f.fechaTimbrado || '', ...soloFiscales(f) })) }
    : { ...form, monto: parseFloat(form.monto), facturas: [] };

  const resetCampos = () => modoFacturas
    ? setForm(f => ({ ...f, facturas: [{ folioCfdi: '', notasCredito: '', monto: '' }], motivoPago: '', observaciones: '' }))
    : setForm(f => ({ ...f, folioCfdi: '', notasCredito: '', monto: '', motivoPago: '', observaciones: '' }));

  const handleGuardar = async () => {
    if (!validate()) { toast_('Completa los campos requeridos', 'error'); return; }
    setLoading(true);
    try {
      const sol = await api.post('/reembolsos', buildPayload());
      toast_(`Reembolso ${sol.folio} guardada`);
      if (onCreated) onCreated();
      resetCampos();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const handlePDF = async () => {
    if (!validate()) { toast_('Completa los campos requeridos', 'error'); return; }
    setLoading(true);
    try {
      const sol = await api.post('/reembolsos', buildPayload());
      const blob = await api.downloadPdf(`/reembolsos/${sol._id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Reembolso_${sol.folio}.pdf` }).click();
      URL.revokeObjectURL(url);
      toast_(`Reembolso ${sol.folio} generada`);
      if (onCreated) onCreated();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <PageHeader
        icon="editar"
        title="Solicitud de Reembolso a Cliente"
        subtitle={`SS-CPP-P-01-F01 · Versión 3 · ${user?.nombre}`}
        actions={
          <>
            <button onClick={() => setPlantillaModal(true)}
              style={{ ...css.btn('secundario'), fontSize: 12 }}><ConIcono nombre="carpeta">Plantillas</ConIcono></button>
            <button onClick={() => setGuardarPlantillaModal(true)}
              style={{ ...css.btn('secundario'), fontSize: 12 }}><ConIcono nombre="guardar">Guardar plantilla</ConIcono></button>
            <button onClick={() => { setModoFacturas(false); setErrors({}); setForm(f => ({ ...f, empresa: '', sucursal: '', centroCostos: '', direccion: '', proveedor: '', banco: '', clabe: '', cuenta: '', folioCfdi: '', notasCredito: '', monto: '', facturas: [], motivoPago: '', observaciones: '', impuestos: [], impuestosImportes: {} })); }}
              style={{ ...css.btn('secundario'), fontSize: 12 }}>Limpiar</button>
            <button onClick={handleGuardar} disabled={loading}
              style={{ ...css.btn('secundario'), fontSize: 12 }}>
              {loading ? '...' : <ConIcono nombre="guardar">Guardar</ConIcono>}
            </button>
            <button onClick={handlePDF} disabled={loading}
              style={{ ...css.btn('secundario'), fontSize: 12, minWidth: 130 }}>
              {loading ? 'Generando...' : <ConIcono nombre="descarga">Guardar y PDF</ConIcono>}
            </button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
        {/* Formulario */}
        <div style={{ ...css.card, padding: 28 }}>
          <SectionHeader>Datos Generales</SectionHeader>
          <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
            <div>
              <Label required>Compañía</Label>
              <select value={form.empresa} onChange={e => handleEmpresa(e.target.value)} style={css.select(errors.empresa)}>
                <option value="">Seleccionar empresa...</option>
                {empresas.map(e => <option key={e._id} value={e.nombre}>{e.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label required>Fecha</Label>
              <input type="date" value={form.fechaSolicitud} readOnly style={{ ...css.input(false), ...css.inputRO }} />
            </div>
          </Grid>
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div>
              <Label required>Sucursal</Label>
              <select value={form.sucursal} onChange={e => handleSucursal(e.target.value)} disabled={!form.empresa} style={css.select(errors.sucursal)}>
                <option value="">Seleccionar...</option>
                {sucursalesFiltradas.map(s => <option key={s._id} value={s.nombre}>{s.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label required>Centro de Costos</Label>
              <select value={form.centroCostos} onChange={e => handleCentro(e.target.value)} disabled={!form.sucursal} style={css.select(errors.centroCostos)}>
                <option value="">Seleccionar...</option>
                {(() => {
                  const secs = [...new Set(centros.map(c => (c.seccion || c.direccion || '').trim()).filter(Boolean))];
                  const sinSec = centros.filter(c => !(c.seccion || c.direccion || '').trim());
                  return [
                    ...secs.map(sec => (
                      <optgroup key={sec} label={sec}>
                        {centros.filter(c => (c.seccion || c.direccion || '').trim() === sec).map(c => (
                          <option key={c._id} value={c.nombre}>{c.nombre}</option>
                        ))}
                      </optgroup>
                    )),
                    ...sinSec.map(c => <option key={c._id} value={c.nombre}>{c.nombre}</option>),
                  ];
                })()}
              </select>
            </div>
            <div>
              <Label>Dirección</Label>
              <select value={form.direccion} onChange={e => set('direccion', e.target.value)} style={css.select(false)}>
                <option value="">Seleccionar...</option>
                {['DIRECCION GENERAL','DIRECCION COMERCIAL','DIRECCION COMPRAS','DIRECCION JURIDICO RH',
                  'DIRECCION FINANZAS','DIRECCION UNE CRISTALERIA','DIRECCION UNE MEDICAL STORE',
                  'DIRECCION LOGISTICA','DIRECCION GOLDEN','DIRECCION PATRIMONIAL','DIRECCION FONTASTIC',
                ].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </Grid>

          <SectionHeader>Cliente / Beneficiario</SectionHeader>
          <div style={{ marginBottom: 14 }}>
            <Label required>Buscar Cliente</Label>
            <ProveedorSearch value={form.proveedor} onSelect={handleProveedor} />
            {errors.proveedor && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Campo requerido</div>}
          </div>
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div><Label>Banco</Label><input value={form.banco} readOnly style={{ ...css.input(false), ...css.inputRO }} /></div>
            <div>
              <Label>CLABE</Label>
              <input value={form.clabe} readOnly style={{ ...css.input(false), ...css.inputRO, fontFamily: 'var(--digit-font)', fontSize: 11.5 }} />
              {form.clabe && (() => {
                const r = validarCLABE(form.clabe);
                if (!r) return null;
                return (
                  <div style={{ fontSize: 11.5, marginTop: 3, fontWeight: 600, color: r.valida ? 'var(--ok)' : 'var(--danger)' }}>
                    <ConIcono nombre={r.valida ? 'check' : 'cerrar'} size={13}>{r.mensaje}</ConIcono>
                  </div>
                );
              })()}
            </div>
            <div><Label>No. Cuenta</Label><input value={form.cuenta} readOnly style={{ ...css.input(false), ...css.inputRO, fontFamily: 'var(--digit-font)', fontSize: 11.5 }} /></div>
          </Grid>

          {/* CFDI / Facturas */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <SectionHeader>{modoFacturas ? 'Facturas / CFDI' : 'CFDI'}</SectionHeader>
            <div style={{ display: 'flex', gap: 6 }}>
              <input ref={xmlInputRef} type="file" accept=".xml,text/xml,application/xml" multiple
                style={{ display: 'none' }} onChange={e => handleImportXML(e.target.files)} />
              <button onClick={() => xmlInputRef.current?.click()} disabled={importando} style={{ ...css.btn('secundario'), padding: '4px 12px', fontSize: 11.5, cursor: importando ? 'wait' : 'pointer' }} title="Lee el XML del SAT y autocompleta folio, monto e impuestos">
                {importando ? 'Leyendo...' : <ConIcono nombre="codigo" size={13}>Importar XML</ConIcono>}
              </button>
              <button onClick={toggleModo} style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 600,
                border: `1.5px solid ${modoFacturas ? 'var(--plasma-line)' : 'var(--hair)'}`,
                background: modoFacturas ? 'var(--plasma-dim)' : 'var(--panel)',
                color: modoFacturas ? 'var(--plasma)' : 'var(--glass)',
                cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}>
                {modoFacturas ? <ConIcono nombre="cerrar" size={13}>Monto simple</ConIcono> : <ConIcono nombre="mas" size={13}>Múltiples facturas</ConIcono>}
              </button>
            </div>
          </div>

          {modoFacturas ? (
            <div style={{ marginBottom: 14 }}>
              <FacturasTable facturas={form.facturas} onChange={handleFacturasChange} hasError={errors.facturas} />
            </div>
          ) : (
            <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
              <div>
                <Label>No. Folio(s) CFDI</Label>
                <input value={form.folioCfdi} onChange={e => {
                  set('folioCfdi', e.target.value);
                  clearTimeout(cfdiTimer.current);
                  cfdiTimer.current = setTimeout(() => checkCfdi(e.target.value), 400);
                }} placeholder="CFDI-001" style={css.input(!!cfdiDuplicado)} />
                {cfdiDuplicado && (
                  <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 3, fontWeight: 600 }}>
                    <Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Ya existe en {cfdiDuplicado.folio} ({cfdiDuplicado.proveedor})
                  </div>
                )}
              </div>
              <div><Label>Notas de Crédito</Label><input value={form.notasCredito} onChange={e => set('notasCredito', e.target.value)} style={css.input(false)} /></div>
            </Grid>
          )}

          <SectionHeader>Datos de Pago</SectionHeader>
          {modoFacturas ? (
            <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
              <div>
                <Label>Moneda</Label>
                <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
                  <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <Label>Forma de Pago</Label>
                <select value={form.formaPago} onChange={e => set('formaPago', e.target.value)} style={css.select(false)}>
                  {['TRANSFERENCIA ELECTRÓNICA', 'CHEQUE', 'EFECTIVO', 'CONTADO', 'REFERENCIA BANCARIA'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <Label>Tipo</Label>
                <select value={form.tipoPago} onChange={e => set('tipoPago', e.target.value)} style={css.select(false)}>
                  <option value="Programado">Programado</option>
                  <option value="No Programado">No Programado</option>
                </select>
              </div>
            </Grid>
          ) : (
            <Grid cols={4} gap={14} style={{ marginBottom: 14 }}>
              <div>
                <Label required>Monto ($)</Label>
                <MontoInput value={form.monto} onChange={v => set('monto', v)} err={errors.monto} />
                {errors.monto && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Monto inválido</div>}
              </div>
              <div>
                <Label>Moneda</Label>
                <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
                  <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <Label>Forma de Pago</Label>
                <select value={form.formaPago} onChange={e => set('formaPago', e.target.value)} style={css.select(false)}>
                  {['TRANSFERENCIA ELECTRÓNICA', 'CHEQUE', 'EFECTIVO', 'CONTADO', 'REFERENCIA BANCARIA'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <Label>Tipo</Label>
                <select value={form.tipoPago} onChange={e => set('tipoPago', e.target.value)} style={css.select(false)}>
                  <option value="Programado">Programado</option>
                  <option value="No Programado">No Programado</option>
                </select>
              </div>
            </Grid>
          )}
          <div style={{ marginBottom: 14 }}>
            <Label required>Motivo de Pago</Label>
            <MotivoSearch value={form.motivoPago} onChange={v => set('motivoPago', v)} hasError={errors.motivoPago} motivos={motivosCatalogo} />
            {errors.motivoPago && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Elige un motivo del catálogo</div>}
            {!motivosCatalogo.length && (
              <div style={{ color: 'var(--st-pendiente)', fontSize: 11.5, marginTop: 3 }}>
                El catálogo de motivos de reembolso está vacío. Pídele a un administrador que lo cargue en Admin → Motivos de Reembolso.
              </div>
            )}
          </div>
          <div>
            <Label required>Concepto del Pago</Label>
            <textarea value={form.observaciones} onChange={e => set('observaciones', e.target.value)} rows={3}
              placeholder="Describe con detalle qué se está pagando (mínimo 50 caracteres)..."
              style={{ ...css.input(errors.observaciones), resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
              {errors.observaciones
                ? <span style={{ color: 'var(--danger)', fontSize: 11.5 }}>Mínimo 50 caracteres — describe el concepto con más detalle</span>
                : <span />}
              <span style={{ fontSize: 11.5, fontFamily: 'var(--digit-font)', color: form.observaciones.trim().length >= 50 ? 'var(--ok)' : 'var(--glass)' }}>
                {form.observaciones.trim().length}/50
              </span>
            </div>
          </div>
        </div>

        {/* Panel resumen */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--panel)', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hair)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}>
            <div style={{ background: 'var(--panel-hi)', borderBottom: '1px solid var(--mesh-soft)', padding: '16px 18px' }}>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Compañía</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginTop: 3 }}>{form.empresa || '—'}</div>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {[['Cliente', form.proveedor], ['Sucursal', form.sucursal], ['Centro Costos', form.centroCostos], ['Banco', form.banco]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--hair-soft)' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', maxWidth: 140, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span>
                </div>
              ))}
              {totalBase > 0 && (() => {
                const { lineas, total } = calcImpuestos(totalBase, form.impuestos, form.impuestosImportes);
                const fmt = n => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
                return (
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hair)' }}>
                    {modoFacturas && (
                      <div style={{ fontSize: 11.5, color: 'var(--glass)', marginBottom: 4 }}>
                        {form.facturas.length} factura{form.facturas.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase' }}>Subtotal</span>
                      <span style={{ fontSize: 12, color: 'var(--ink-soft)', fontFamily: 'var(--digit-font)' }}>{fmt(totalBase)}</span>
                    </div>
                    {lineas.map(l => (
                      <div key={l.clave} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: 11.5, color: l.esRetencion ? 'var(--danger)' : 'var(--ok)' }}>{l.label}</span>
                        <span style={{ fontSize: 11.5, color: l.esRetencion ? 'var(--danger)' : 'var(--ok)', fontWeight: 600, fontFamily: 'var(--digit-font)' }}>
                          {l.esRetencion ? `-${fmt(l.valor)}` : `+${fmt(l.valor)}`}
                        </span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--hair-soft)' }}>
                      <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Neto a Pagar</span>
                      <div>
                        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--plasma)', transition: 'color 0.3s', fontFamily: 'var(--digit-font)' }}>{fmt(total)}</span>
                        <span style={{ fontSize: 11.5, color: 'var(--glass)', marginLeft: 4 }}>{form.moneda}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <button onClick={handlePDF} disabled={loading}
            style={{ ...css.btn('primario'), padding: '13px', fontSize: 13.5, width: '100%' }}>
            {loading ? 'Generando...' : <ConIcono nombre="descarga">Guardar y PDF</ConIcono>}
          </button>
        </div>
      </div>

      {/* Modal: cargar plantilla */}
      <Modal open={plantillaModal} onClose={() => setPlantillaModal(false)} title="Plantillas guardadas" width={460}>
        {plantillas.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--glass)', padding: '24px 0', fontSize: 13 }}>
            No tienes plantillas guardadas.<br />Rellena el formulario y usa "Guardar plantilla".
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plantillas.map(p => (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{p.nombre}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--glass)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[p.proveedor, p.empresa, p.motivoPago].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button onClick={() => aplicarPlantilla(p)} style={{ ...css.btn('secundario'), fontSize: 12, padding: '5px 12px', flexShrink: 0 }}>Aplicar</button>
                <button aria-label="Eliminar plantilla" title="Eliminar plantilla" onClick={() => eliminarPlantilla(p._id)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}><Icono nombre="papelera" size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Modal: guardar plantilla */}
      <Modal open={guardarPlantillaModal} onClose={() => { setGuardarPlantillaModal(false); setNombrePlantilla(''); }} title="Guardar como plantilla" width={380}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>Se guardarán los datos del proveedor, empresa, motivo y configuración de pago (sin montos ni folios CFDI).</div>
          <input autoFocus value={nombrePlantilla} onChange={e => setNombrePlantilla(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && guardarComoPlantilla()}
            placeholder="Nombre de la plantilla..." style={css.input(!nombrePlantilla.trim())} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setGuardarPlantillaModal(false); setNombrePlantilla(''); }} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={guardarComoPlantilla} disabled={!nombrePlantilla.trim()} style={{ ...css.btn('primario'), fontSize: 13 }}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
